import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Stack, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import AppSearch from '../../components/base/AppSearch';
import PatientTable from '../Patients/components/PatientTable';
import AddPatientDialog from '../../pages/Patients/components/AddPatient/AddPatientDialog';
import EditPatientDialog from './components/EditPatientDialog'; // 👈 Nouveau
import ViewPatientDialog from './components/ViewPatientDialog';

const ITEMS_PER_PAGE = 8;

const Patients = () => {
  const { user } = useAuth();
  const [allPatients, setAllPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [editingPatientId, setEditingPatientId] = useState(null); // 👈 juste l'ID
  const [viewingPatientId, setViewingPatientId] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');

  const fetchPatients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/admin/patients`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const rawData = Array.isArray(response.data)
        ? response.data
        : response.data?.data || response.data?.patients || [];

      setAllPatients(rawData.filter(Boolean));
    } catch (err) {
      console.error('Erreur attrapée :', err);
      toast.error('Erreur de chargement des patients');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token]);

  useEffect(() => {
    if (user) fetchPatients();
  }, [user, fetchPatients]);

  const filteredPatients = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return allPatients.filter((patient) => {
      const nom = patient.nom_patient || '';
      const prenom = patient.prenom_patient || '';
      return `${nom} ${prenom}`.toLowerCase().includes(term);
    });
  }, [allPatients, searchTerm]);

  const totalItems = filteredPatients.length;
  const lastPage = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));

  const safePage = currentPage > lastPage ? 1 : currentPage;
  useEffect(() => {
    if (safePage !== currentPage) setCurrentPage(safePage);
  }, [safePage, currentPage]);

  const displayedPatients = useMemo(() => {
    const begin = (safePage - 1) * ITEMS_PER_PAGE;
    return filteredPatients.slice(begin, begin + ITEMS_PER_PAGE);
  }, [filteredPatients, safePage]);

  const handleView = (patient) => {
    setViewingPatientId(patient.id_patient_profile);
  };

  const handleEdit = (patient) => {
    setEditingPatientId(patient.id_patient_profile); // 👈 le dialogue ira chercher le détail lui-même
  };

  const handleDelete = async (patient) => {
    if (
      window.confirm(
        `Voulez-vous vraiment supprimer le patient ${patient.nom_patient} ?`
      )
    ) {
      try {
        await axios.delete(
          `${apiUrl}/admin/patients/${patient.id_patient_profile}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        toast.success('Patient supprimé avec succès');
        fetchPatients();
      } catch (err) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 4, flexWrap: 'wrap', gap: 2 }}
      >
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#2B3674' }}>
          Gestion des Patients
        </Typography>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="flex-end" // <- C'est cette ligne qui pousse tout vers la droite
          sx={{ flexWrap: 'nowrap', width: '100%' }}
        >
          <Box sx={{ width: '300px', minWidth: 0 }}>
            <AppSearch
              value={searchTerm}
              onChange={(val) => {
                setSearchTerm(val);
                setCurrentPage(1);
              }}
              placeholder="Rechercher un patient..."
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddModal(true)}
            sx={{
              backgroundColor: '#4318FF',
              fontWeight: 700,
              borderRadius: '12px',
              textTransform: 'none',
              px: 3,
              py: 1,
              flexShrink: 0,
              '&:hover': { backgroundColor: '#3311CC' },
            }}
          >
            Ajouter un patient
          </Button>
        </Stack>
      </Stack>

      <PatientTable
        loading={loading}
        currentData={displayedPatients}
        currentPage={safePage}
        count={lastPage}
        totalItems={totalItems}
        itemsPerPage={ITEMS_PER_PAGE}
        apiUrl={apiUrl}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onPageChange={(newPage) => setCurrentPage(newPage)}
      />

      <AddPatientDialog
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onPatientAdded={fetchPatients}
        apiUrl={apiUrl}
        token={token}
      />

      <EditPatientDialog
        open={!!editingPatientId}
        patientId={editingPatientId}
        onClose={() => setEditingPatientId(null)}
        onPatientUpdated={fetchPatients}
        apiUrl={apiUrl}
        token={token}
      />
      <ViewPatientDialog
        open={!!viewingPatientId}
        patientId={viewingPatientId}
        onClose={() => setViewingPatientId(null)}
        apiUrl={apiUrl}
        token={token}
      />
    </Box>
  );
};

export default Patients;
